<script setup>
import { ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PlaygroundTab from './PlaygroundTab.vue';

const props = defineProps({
  agent: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['saved']);

const store = useStore();
const { t } = useI18n();
const alert = useAlert;

const steps = ref([]);
const showHelp = ref(false);
const userInputExample = ref('{{user_input}}');

const flowTemplates = [
  {
    key: 'simple_sale',
    label: t('AI_AGENTS.FLOW.TEMPLATE_SIMPLE_SALE'),
    icon: 'i-lucide-shopping-cart',
    category: 'sales',
    steps: [
      {
        title: 'Saludar al cliente',
        type: 'action',
        instruction:
          'Saluda al cliente de forma amable y pregúntale en qué puedes ayudarle hoy.',
      },
      {
        title: 'Buscar producto solicitado',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: true,
      },
      {
        title: 'Preguntar si necesita algo más',
        type: 'action',
        instruction:
          'Pregunta al cliente si necesita información adicional o si quiere comprar el producto.',
      },
    ],
  },
  {
    key: 'quick_sale',
    label: t('AI_AGENTS.FLOW.TEMPLATE_QUICK_SALE'),
    icon: 'i-lucide-zap',
    category: 'sales',
    steps: [
      {
        title: 'Saludar directo',
        type: 'action',
        instruction:
          'Saluda al cliente de forma breve y directa, pregúntale qué producto quiere comprar.',
      },
      {
        title: 'Buscar y mostrar producto',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: true,
      },
      {
        title: 'Cierre directo',
        type: 'action',
        instruction:
          'Dile el precio, pregunta si quiere comprar y dale el link o número para hacer el pedido.',
      },
    ],
  },
  {
    key: 'lead_qualification',
    label: t('AI_AGENTS.FLOW.TEMPLATE_LEAD_QUALIFICATION'),
    icon: 'i-lucide-target',
    category: 'sales',
    steps: [
      {
        title: 'Saludar y presentar opciones',
        type: 'action',
        instruction:
          'Saluda al cliente y pregúntale si busca información para sí mismo, para su empresa o para un tercero.',
      },
      {
        title: 'Preguntar presupuesto',
        type: 'action',
        instruction:
          'Pregunta de forma amable cuál es su rango de presupuesto aproximado para poder recomendarle lo mejor.',
      },
      {
        title: 'Preguntar urgencia',
        type: 'action',
        instruction:
          'Pregunta si necesita el producto para ahora, esta semana o solo está investigando opciones.',
      },
      {
        title: 'Buscar producto según necesidad',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: true,
      },
      {
        title: 'Ofrecer seguimiento',
        type: 'action',
        instruction:
          'Si el cliente está investigando, ofrece enviarle más información por correo o WhatsApp. Si quiere comprar, ofrece pasarle a un asesor.',
      },
    ],
  },
  {
    key: 'promotion_push',
    label: t('AI_AGENTS.FLOW.TEMPLATE_PROMOTION_PUSH'),
    icon: 'i-lucide-megaphone',
    category: 'sales',
    steps: [
      {
        title: 'Saludar y anunciar promoción',
        type: 'action',
        instruction:
          'Saluda al cliente de forma entusiasta y menciona que tenemos una promoción especial activa en este momento.',
      },
      {
        title: 'Preguntar interés en promoción',
        type: 'action',
        instruction:
          'Pregunta si le gustaría conocer los productos que tienen promoción activa.',
      },
      {
        title: 'Buscar productos en promoción',
        type: 'search_product',
        product_query: 'promoción {{user_input}}',
        include_media: true,
      },
      {
        title: 'Crear urgencia',
        type: 'action',
        instruction:
          'Menciona que la promoción es por tiempo limitado y pregunta si quiere reservar o comprar ahora.',
      },
    ],
  },
  {
    key: 'abandoned_cart',
    label: t('AI_AGENTS.FLOW.TEMPLATE_ABANDONED_CART'),
    icon: 'i-lucide-refresh-ccw',
    category: 'sales',
    steps: [
      {
        title: 'Recordar producto visto',
        type: 'action',
        instruction:
          'Saluda al cliente mencionando que notamos su interés en un producto y pregúntale si necesita ayuda para finalizar.',
      },
      {
        title: 'Buscar producto de interés',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: true,
      },
      {
        title: 'Ofrecer asistencia o descuento',
        type: 'condition',
        condition: 'El cliente menciona duda, precio alto o no está seguro',
        true_action:
          'Ofrece un descuento especial o ayuda con dudas para cerrar la venta',
        false_action:
          'Pregunta si hay algo más que le impida comprar y ofrece ayuda',
      },
      {
        title: 'Cierre suave',
        type: 'action',
        instruction:
          'Pregunta si quiere que un asesor lo contacte para finalizar la compra o si prefiere comprar directamente.',
      },
    ],
  },
  {
    key: 'product_comparison',
    label: t('AI_AGENTS.FLOW.TEMPLATE_PRODUCT_COMPARISON'),
    icon: 'i-lucide-scale',
    category: 'sales',
    steps: [
      {
        title: 'Preguntar producto principal',
        type: 'action',
        instruction:
          'Pregunta al cliente qué producto le interesa comparar o qué opciones está considerando.',
      },
      {
        title: 'Buscar producto principal',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: true,
      },
      {
        title: 'Preguntar alternativa',
        type: 'action',
        instruction:
          'Pregunta si quiere comparar con otra opción, modelo o categoría específica.',
      },
      {
        title: 'Buscar producto alternativo',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: true,
      },
      {
        title: 'Resumen comparativo',
        type: 'action',
        instruction:
          'Resume las diferencias clave entre las opciones mostradas: precio, características, disponibilidad y recomienda la mejor según el caso.',
      },
    ],
  },
  {
    key: 'close_sale',
    label: t('AI_AGENTS.FLOW.TEMPLATE_CLOSE_SALE'),
    icon: 'i-lucide-badge-dollar-sign',
    category: 'sales',
    steps: [
      {
        title: 'Confirmar interés',
        type: 'action',
        instruction:
          'Confirma con el cliente que el producto que mostraste le interesa y pregunta si tiene alguna duda antes de comprar.',
      },
      {
        title: 'Manejar objeciones',
        type: 'condition',
        condition:
          'El cliente menciona precio alto, duda, necesita pensarlo o tiene una objeción',
        true_action:
          'Ofrece un descuento especial, facilidades de pago o garantía extendida para superar la objeción',
        false_action:
          'Pregunta si está listo para comprar y guialo al siguiente paso',
      },
      {
        title: 'Buscar producto a cerrar',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: false,
      },
      {
        title: 'Llamado a la acción',
        type: 'action',
        instruction:
          'Dale instrucciones claras para comprar: link, número de WhatsApp, o indica que un asesor lo contactará para finalizar.',
      },
    ],
  },
  {
    key: 'upsell',
    label: t('AI_AGENTS.FLOW.TEMPLATE_UPSELL'),
    icon: 'i-lucide-trending-up',
    category: 'sales',
    steps: [
      {
        title: 'Buscar producto solicitado',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: true,
      },
      {
        title: 'Preguntar uso o contexto',
        type: 'action',
        instruction:
          'Pregunta al cliente para qué va a usar el producto o en qué contexto lo necesita.',
      },
      {
        title: 'Ofrecer upgrade o complemento',
        type: 'condition',
        condition:
          'El cliente menciona que quiere algo más potente, mejor, o que lo va a usar intensivamente',
        true_action:
          'Muestra un producto superior o complemento que mejore la experiencia',
        false_action:
          'Confirma que el producto elegido es el ideal y pregunta si quiere agregar algo más',
      },
      {
        title: 'Buscar producto complementario',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: true,
      },
      {
        title: 'Cierre con bundle',
        type: 'action',
        instruction:
          'Resume la oferta completa (producto principal + complemento) y menciona cualquier descuento por comprar ambos.',
      },
    ],
  },
  {
    key: 'follow_up_quote',
    label: t('AI_AGENTS.FLOW.TEMPLATE_FOLLOW_UP_QUOTE'),
    icon: 'i-lucide-mail',
    category: 'sales',
    steps: [
      {
        title: 'Saludo de seguimiento',
        type: 'action',
        instruction:
          'Saluda al cliente mencionando que le enviaste información o una cotización anteriormente y pregúntale si tiene dudas.',
      },
      {
        title: 'Buscar producto cotizado',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: true,
      },
      {
        title: 'Preguntar decisión',
        type: 'condition',
        condition:
          'El cliente dice que está listo, quiere comprar o acepta la cotización',
        true_action: 'Guialo al cierre: pide datos de envío o método de pago',
        false_action:
          'Pregunta qué le falta para decidirse y ofrece resolver dudas o ajustar la cotización',
      },
      {
        title: 'Cierre o nurturing',
        type: 'action',
        instruction:
          'Si compra, confirma el pedido. Si no, ofrece enviarle más info o contactarlo en unos días.',
      },
    ],
  },
  {
    key: 'cold_lead_recovery',
    label: t('AI_AGENTS.FLOW.TEMPLATE_COLD_LEAD_RECOVERY'),
    icon: 'i-lucide-snowflake',
    category: 'sales',
    steps: [
      {
        title: 'Reactivar conversación',
        type: 'action',
        instruction:
          'Saluda al cliente de forma amable mencionando que hace tiempo no conversan y pregúntale si sigue interesado en solucionar su necesidad.',
      },
      {
        title: 'Novedad o promo',
        type: 'action',
        instruction:
          'Menciona brevemente una novedad, mejora o promoción reciente que pueda ser relevante para su interés anterior.',
      },
      {
        title: 'Buscar producto de interés previo',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: true,
      },
      {
        title: 'Cita o cierre',
        type: 'condition',
        condition: 'El cliente muestra interés renovado',
        true_action:
          'Propón una llamada o cita para mostrarle todo lo nuevo y cerrar la venta',
        false_action:
          'Agradece su tiempo y ofrece contactarlo en el futuro si algo cambia',
      },
    ],
  },
  {
    key: 'consultative_sale',
    label: t('AI_AGENTS.FLOW.TEMPLATE_CONSULTATIVE_SALE'),
    icon: 'i-lucide-stethoscope',
    category: 'sales',
    steps: [
      {
        title: 'Descubrir problema',
        type: 'action',
        instruction:
          'Pregunta al cliente qué problema está intentando resolver o qué necesidad tiene que no está cubierta actualmente.',
      },
      {
        title: 'Explorar contexto',
        type: 'action',
        instruction:
          'Haz preguntas de contexto: ¿quién lo usará?, ¿cuántos?, ¿qué características son indispensables?, ¿cuál es el presupuesto?',
      },
      {
        title: 'Buscar solución recomendada',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: true,
      },
      {
        title: 'Explicar por qué esta solución',
        type: 'action',
        instruction:
          'Conecta las características del producto con las necesidades que el cliente describió. Explica por qué esta es la mejor opción para su caso.',
      },
      {
        title: 'Cierre consultivo',
        type: 'action',
        instruction:
          'Resume los beneficios específicos para su caso, pregunta si tiene dudas y ofrece el siguiente paso para adquirirlo.',
      },
    ],
  },
  {
    key: 'info_only',
    label: t('AI_AGENTS.FLOW.TEMPLATE_INFO_ONLY'),
    icon: 'i-lucide-file-text',
    category: 'basic',
    steps: [
      {
        title: 'Saludar y preguntar producto',
        type: 'action',
        instruction:
          'Saluda al cliente y pregúntale qué producto le interesa conocer.',
      },
      {
        title: 'Buscar y responder solo información',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: false,
      },
      {
        title: 'Despedirse',
        type: 'action',
        instruction: 'Agradece al cliente y despídete amablemente.',
      },
    ],
  },
  {
    key: 'conditional_image',
    label: t('AI_AGENTS.FLOW.TEMPLATE_CONDITIONAL'),
    icon: 'i-lucide-git-branch',
    category: 'basic',
    steps: [
      {
        title: 'Saludar al cliente',
        type: 'action',
        instruction: 'Saluda al cliente y pregúntale qué producto busca.',
      },
      {
        title: '¿Pide foto o imagen?',
        type: 'condition',
        condition: 'El cliente pide foto, imagen o quiere ver el producto',
        true_action: 'Continuar con búsqueda incluyendo imagen',
        false_action: 'Continuar con búsqueda solo de información',
      },
      {
        title: 'Buscar con imagen',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: true,
      },
      {
        title: 'Despedirse',
        type: 'action',
        instruction: 'Agradece al cliente y ofrece ayuda adicional.',
      },
    ],
  },
  {
    key: 'support_basic',
    label: t('AI_AGENTS.FLOW.TEMPLATE_SUPPORT'),
    icon: 'i-lucide-headphones',
    category: 'basic',
    steps: [
      {
        title: 'Preguntar nombre',
        type: 'action',
        instruction:
          'Pregunta amablemente el nombre del cliente para personalizar la atención.',
      },
      {
        title: 'Preguntar qué necesita',
        type: 'action',
        instruction: 'Pregunta al cliente qué producto o información necesita.',
      },
      {
        title: 'Buscar producto',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: true,
      },
      {
        title: 'Ofrecer ayuda adicional',
        type: 'action',
        instruction:
          'Pregunta si necesita algo más o si quiere hablar con un humano.',
      },
    ],
  },
  {
    key: 'onboarding',
    label: t('AI_AGENTS.FLOW.TEMPLATE_ONBOARDING'),
    icon: 'i-lucide-rocket',
    category: 'basic',
    steps: [
      {
        title: 'Bienvenida y presentación',
        type: 'action',
        instruction:
          'Da la bienvenida al cliente, preséntate como asistente virtual y explica que puedes ayudarle a conocer todos los productos y servicios.',
      },
      {
        title: 'Preguntar necesidad principal',
        type: 'action',
        instruction:
          'Pregunta al cliente qué necesita resolver o qué tipo de producto o servicio está buscando.',
      },
      {
        title: 'Buscar producto o servicio',
        type: 'search_product',
        product_query: '{{user_input}}',
        include_media: true,
      },
      {
        title: 'Explicar siguiente paso',
        type: 'action',
        instruction:
          'Explica claramente qué debe hacer el cliente para comprar, contratar o obtener más información. Ofrece contactar a un humano si lo prefiere.',
      },
    ],
  },
];

const templateCategories = [
  { key: 'sales', label: t('AI_AGENTS.FLOW.TEMPLATE_CATEGORY_SALES') },
  { key: 'basic', label: t('AI_AGENTS.FLOW.TEMPLATE_CATEGORY_BASIC') },
];

const applyTemplate = templateKey => {
  const template = flowTemplates.find(item => item.key === templateKey);
  if (!template) return;

  template.steps.forEach(step => {
    steps.value.push({
      id: `step_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      title: step.title || '',
      type: step.type,
      instruction: step.instruction || '',
      condition: step.condition || '',
      true_action: step.true_action || '',
      false_action: step.false_action || '',
      product_query: step.product_query || '',
      include_media: step.include_media || false,
    });
  });
};

const resetSteps = () => {
  const cloned = JSON.parse(JSON.stringify(props.agent));
  steps.value = Array.isArray(cloned.config?.flow_steps)
    ? cloned.config.flow_steps
    : [];
};

watch(() => props.agent, resetSteps, { immediate: true, deep: true });

const addStep = (type = 'action') => {
  steps.value.push({
    id: `step_${Date.now()}`,
    title: '',
    type,
    instruction: '',
    condition: '',
    true_action: '',
    false_action: '',
    product_query: '',
  });
};

const removeStep = index => {
  steps.value.splice(index, 1);
};

const moveStep = (index, direction) => {
  const newIndex = index + direction;
  if (newIndex < 0 || newIndex >= steps.value.length) return;
  const temp = steps.value[index];
  steps.value[index] = steps.value[newIndex];
  steps.value[newIndex] = temp;
};

const addThenAction = index => {
  const step = steps.value[index];
  if (!step.true_action) step.true_action = '';
  step.true_action += step.true_action ? '\n- ' : '- ';
};

const addElseAction = index => {
  const step = steps.value[index];
  if (!step.false_action) step.false_action = '';
  step.false_action += step.false_action ? '\n- ' : '- ';
};

const save = async () => {
  try {
    await store.dispatch('aiAgents/update', {
      id: props.agent.id,
      data: {
        config: {
          ...props.agent.config,
          flow_steps: steps.value,
        },
      },
    });
    alert(t('AI_AGENTS.MESSAGES.UPDATE_SUCCESS'));
    emit('saved');
  } catch (error) {
    alert(t('AI_AGENTS.MESSAGES.UPDATE_ERROR'));
  }
};
</script>

<template>
  <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
    <!-- Columna izquierda: Lista de pasos -->
    <div class="grid gap-4 min-w-0">
      <div class="flex items-center justify-between">
        <h2 class="text-sm font-semibold text-n-slate-12">
          {{ t('AI_AGENTS.FLOW.TITLE') }}
        </h2>
      </div>

      <p class="text-xs text-n-slate-10">
        {{ t('AI_AGENTS.FLOW.DESCRIPTION') }}
      </p>

      <div class="rounded-lg border border-n-slate-6 bg-n-slate-1/50 p-3">
        <button
          class="flex items-center gap-2 text-xs font-medium text-n-brand hover:text-n-brand/80 w-full"
          @click="showHelp = !showHelp"
        >
          <span class="i-lucide-info w-4 h-4" />
          {{ t('AI_AGENTS.FLOW.HELP_TITLE') }}
          <span
            class="i-lucide-chevron-down w-3 h-3 ml-auto transition-transform"
            :class="showHelp ? 'rotate-180' : ''"
          />
        </button>
        <div v-if="showHelp" class="mt-2 text-xs text-n-slate-10 space-y-2">
          <div>
            <p class="font-medium text-n-slate-11 mb-1">
              {{ t('AI_AGENTS.FLOW.HELP_STEPS_TITLE') }}
            </p>
            <ul class="list-disc list-inside space-y-0.5">
              <li>{{ t('AI_AGENTS.FLOW.HELP_STEP_ACTION') }}</li>
              <li>{{ t('AI_AGENTS.FLOW.HELP_STEP_CONDITION') }}</li>
              <li>{{ t('AI_AGENTS.FLOW.HELP_STEP_PRODUCT') }}</li>
            </ul>
          </div>
          <div>
            <p class="font-medium text-n-slate-11 mb-1">
              {{ t('AI_AGENTS.FLOW.HELP_VARIABLES_TITLE') }}
            </p>
            <p class="text-n-slate-9">
              {{ t('AI_AGENTS.FLOW.HELP_VARIABLES_DESC') }}
            </p>
            <code
              class="block mt-1 px-2 py-1 rounded bg-n-slate-3 text-n-slate-11 text-[10px] font-mono"
            >
              {{ userInputExample }}
            </code>
          </div>
          <div>
            <p class="font-medium text-n-slate-11 mb-1">
              {{ t('AI_AGENTS.FLOW.HELP_EXAMPLES_TITLE') }}
            </p>
            <div class="space-y-1.5">
              <div class="rounded border border-n-slate-5 p-2">
                <p class="font-medium text-n-slate-11 text-[10px]">
                  {{ t('AI_AGENTS.FLOW.HELP_EXAMPLE_1_NAME') }}
                </p>
                <p class="text-[10px] text-n-slate-9">
                  {{ t('AI_AGENTS.FLOW.HELP_EXAMPLE_1_DESC') }}
                </p>
              </div>
              <div class="rounded border border-n-slate-5 p-2">
                <p class="font-medium text-n-slate-11 text-[10px]">
                  {{ t('AI_AGENTS.FLOW.HELP_EXAMPLE_2_NAME') }}
                </p>
                <p class="text-[10px] text-n-slate-9">
                  {{ t('AI_AGENTS.FLOW.HELP_EXAMPLE_2_DESC') }}
                </p>
              </div>
              <div class="rounded border border-n-slate-5 p-2">
                <p class="font-medium text-n-slate-11 text-[10px]">
                  {{ t('AI_AGENTS.FLOW.HELP_EXAMPLE_3_NAME') }}
                </p>
                <p class="text-[10px] text-n-slate-9">
                  {{ t('AI_AGENTS.FLOW.HELP_EXAMPLE_3_DESC') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="grid gap-4">
        <div v-if="!steps.length" class="text-sm text-n-slate-10 py-4">
          {{ t('AI_AGENTS.FLOW.NO_STEPS') }}
        </div>

        <div v-else class="grid gap-4">
          <!-- Panel de lista de pasos -->
          <div
            class="rounded-xl border border-n-slate-6 bg-n-surface-1 p-3 overflow-hidden"
          >
            <div class="text-xs font-semibold text-n-slate-11 mb-3 px-1">
              {{
                t('AI_AGENTS.FLOW.STEPS_LIST_TITLE_WITH_COUNT', {
                  count: steps.length,
                })
              }}
            </div>
            <div class="grid gap-1.5 overflow-hidden">
              <div
                v-for="(step, index) in steps"
                :key="step.id"
                class="flex items-center gap-2 rounded-lg px-2 py-2 hover:bg-n-slate-6/50 transition-colors"
              >
                <span
                  class="i-lucide-grip-vertical w-3 h-3 text-n-slate-8 cursor-move"
                />
                <span
                  class="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0"
                  :class="
                    step.type === 'condition'
                      ? 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300'
                      : 'bg-n-slate-7 text-n-slate-11'
                  "
                >
                  {{ index + 1 }}
                </span>
                <span class="text-xs text-n-slate-11 truncate flex-1 min-w-0">
                  {{
                    step.title ||
                    (step.type === 'condition'
                      ? t('AI_AGENTS.FLOW.CONDITION_LABEL')
                      : step.type === 'search_product'
                        ? t('AI_AGENTS.FLOW.PRODUCT_SEARCH_LABEL')
                        : t('AI_AGENTS.FLOW.ACTION_LABEL'))
                  }}
                </span>
                <span
                  v-if="step.type === 'condition'"
                  class="i-lucide-git-branch w-3 h-3 text-purple-500 flex-shrink-0"
                />
                <div class="flex items-center gap-0.5 flex-shrink-0">
                  <button
                    class="p-1 text-n-slate-9 hover:text-n-slate-12 disabled:opacity-30"
                    :disabled="index === 0"
                    @click="moveStep(index, -1)"
                  >
                    <span class="i-lucide-chevron-up w-3 h-3" />
                  </button>
                  <button
                    class="p-1 text-n-slate-9 hover:text-n-slate-12 disabled:opacity-30"
                    :disabled="index === steps.length - 1"
                    @click="moveStep(index, 1)"
                  >
                    <span class="i-lucide-chevron-down w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <button
            class="w-full px-3 py-1.5 text-xs font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90"
            @click="addStep('action')"
          >
            <span class="i-lucide-plus w-3 h-3 inline-block" />
            {{ t('AI_AGENTS.FLOW.ADD_ACTION') }}
          </button>
          <button
            class="w-full px-3 py-1.5 text-xs font-medium rounded-lg bg-n-amber-9 text-white hover:bg-n-amber-10"
            @click="addStep('condition')"
          >
            <span class="i-lucide-git-branch w-3 h-3 inline-block" />
            {{ t('AI_AGENTS.FLOW.ADD_CONDITION') }}
          </button>
          <button
            class="w-full px-3 py-1.5 text-xs font-medium rounded-lg bg-n-teal-9 text-white hover:bg-n-teal-10"
            @click="addStep('search_product')"
          >
            <span class="i-lucide-search w-3 h-3 inline-block" />
            {{ t('AI_AGENTS.FLOW.ADD_PRODUCT_SEARCH') }}
          </button>
        </div>

        <div class="mt-2 pt-3 border-t border-n-slate-6">
          <p class="text-xs font-medium text-n-slate-11 mb-2">
            {{ t('AI_AGENTS.FLOW.TEMPLATES_TITLE') }}
          </p>
          <div class="space-y-3">
            <div v-for="category in templateCategories" :key="category.key">
              <p
                class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider mb-1.5 px-1"
              >
                {{ category.label }}
              </p>
              <div class="grid grid-cols-2 gap-2">
                <button
                  v-for="template in flowTemplates.filter(
                    item => item.category === category.key
                  )"
                  :key="template.key"
                  class="flex items-center gap-1.5 px-2 py-1.5 text-[10px] font-medium rounded-lg bg-n-slate-3 hover:bg-n-slate-4 text-n-slate-11 transition-colors"
                  @click="applyTemplate(template.key)"
                >
                  <span :class="template.icon" class="w-3 h-3 flex-shrink-0" />
                  <span class="truncate">{{ template.label }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Columna centro: Detalles de pasos -->
    <div class="grid gap-4 min-w-0">
      <div v-if="!steps.length" class="text-sm text-n-slate-10 py-4">
        {{ t('AI_AGENTS.FLOW.NO_STEPS_DETAIL') }}
      </div>

      <div v-else class="grid gap-3">
        <div
          v-for="(step, index) in steps"
          :key="step.id"
          class="rounded-xl border border-n-slate-6 bg-n-surface-1 p-4"
        >
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <span class="text-xs font-semibold text-n-brand">
                {{ index + 1 }}
              </span>
              <span
                class="text-xs px-2 py-0.5 rounded-full bg-n-slate-7 text-n-slate-11"
              >
                {{
                  step.type === 'condition'
                    ? t('AI_AGENTS.FLOW.CONDITION_LABEL')
                    : step.type === 'search_product'
                      ? t('AI_AGENTS.FLOW.PRODUCT_SEARCH_LABEL')
                      : t('AI_AGENTS.FLOW.ACTION_LABEL')
                }}
              </span>
            </div>
            <div class="flex items-center gap-1">
              <button
                class="p-1 text-n-slate-10 hover:text-n-slate-12 disabled:opacity-30"
                :disabled="index === 0"
                @click="moveStep(index, -1)"
              >
                <span class="i-lucide-arrow-up w-3 h-3" />
              </button>
              <button
                class="p-1 text-n-slate-10 hover:text-n-slate-12 disabled:opacity-30"
                :disabled="index === steps.length - 1"
                @click="moveStep(index, 1)"
              >
                <span class="i-lucide-arrow-down w-3 h-3" />
              </button>
              <button
                class="p-1 text-n-slate-10 hover:text-n-ruby-9"
                @click="removeStep(index)"
              >
                <span class="i-lucide-trash-2 w-3 h-3" />
              </button>
            </div>
          </div>

          <label class="grid gap-1 mb-3">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('AI_AGENTS.FLOW.STEP_TITLE') }}
            </span>
            <input
              v-model="step.title"
              type="text"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
              :placeholder="t('AI_AGENTS.FLOW.STEP_TITLE_PLACEHOLDER')"
            />
          </label>

          <div v-if="step.type === 'action'" class="grid gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('AI_AGENTS.FLOW.ACTION_INSTRUCTION') }}
            </span>
            <textarea
              v-model="step.instruction"
              rows="3"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
              :placeholder="t('AI_AGENTS.FLOW.ACTION_INSTRUCTION_PLACEHOLDER')"
            />
          </div>

          <div v-else-if="step.type === 'search_product'" class="grid gap-2">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('AI_AGENTS.FLOW.PRODUCT_QUERY_LABEL') }}
            </span>
            <input
              v-model="step.product_query"
              type="text"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-teal-8 dark:border-n-teal-9 bg-n-teal-9/10 text-n-slate-12"
              :placeholder="t('AI_AGENTS.FLOW.PRODUCT_QUERY_PLACEHOLDER')"
            />
            <p class="text-xs text-n-slate-10">
              {{ t('AI_AGENTS.FLOW.PRODUCT_QUERY_HINT') }}
            </p>
            <label class="flex items-center gap-2 mt-1">
              <input
                v-model="step.include_media"
                type="checkbox"
                class="rounded border-n-slate-6 w-4 h-4 accent-n-teal-9"
              />
              <span class="text-xs text-n-slate-10">
                {{ t('AI_AGENTS.FLOW.INCLUDE_PRODUCT_MEDIA') }}
              </span>
            </label>
          </div>

          <div v-else class="grid gap-3">
            <!-- Sección: Condición -->
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <span
                  class="i-lucide-bot w-4 h-4 text-purple-500 dark:text-purple-400"
                />
                <span
                  class="text-sm font-medium text-purple-600 dark:text-purple-400"
                >
                  {{ t('AI_AGENTS.FLOW.CONDITION_IF') }}
                </span>
              </div>
              <input
                v-model="step.condition"
                type="text"
                class="w-full px-3 py-2 text-sm rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/30 text-n-slate-12"
                :placeholder="t('AI_AGENTS.FLOW.CONDITION_TEXT_PLACEHOLDER')"
              />
            </div>

            <!-- Sección: Entonces -->
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <span
                  class="i-lucide-corner-down-right w-4 h-4 text-green-500 dark:text-green-400"
                />
                <span
                  class="text-sm font-medium text-green-600 dark:text-green-400"
                >
                  {{ t('AI_AGENTS.FLOW.CONDITION_THEN') }}
                </span>
              </div>
              <textarea
                v-model="step.true_action"
                rows="2"
                class="w-full px-3 py-2 text-sm rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 text-n-slate-12"
                :placeholder="t('AI_AGENTS.FLOW.TRUE_ACTION_PLACEHOLDER')"
              />
              <button
                class="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium"
                @click="addThenAction(index)"
              >
                <span class="i-lucide-plus w-3 h-3" />
                {{ t('AI_AGENTS.FLOW.ADD_ACTION') }}
              </button>
            </div>

            <!-- Sección: Si no -->
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <span
                  class="i-lucide-arrow-right w-4 h-4 text-red-500 dark:text-red-400"
                />
                <span
                  class="text-sm font-medium text-red-600 dark:text-red-400"
                >
                  {{ t('AI_AGENTS.FLOW.CONDITION_ELSE') }}
                </span>
              </div>
              <textarea
                v-model="step.false_action"
                rows="2"
                class="w-full px-3 py-2 text-sm rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 text-n-slate-12"
                :placeholder="t('AI_AGENTS.FLOW.FALSE_ACTION_PLACEHOLDER')"
              />
              <button
                class="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium"
                @click="addElseAction(index)"
              >
                <span class="i-lucide-plus w-3 h-3" />
                {{ t('AI_AGENTS.FLOW.ADD_ACTION') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end">
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90"
          @click="save"
        >
          {{ t('AI_AGENTS.FORM.SAVE') }}
        </button>
      </div>
    </div>

    <!-- Columna derecha: Playground -->
    <PlaygroundTab :agent="agent" />
  </div>
</template>
